// Client helpers for durable background crawl jobs (server: server/crawlJobs.ts).
// A crawl is started as a job, then the client subscribes to its SSE stream. If the
// browser closes/refreshes, the job keeps running server-side and its results are saved
// to the user's account; on return, listActiveJobs() lets the UI reattach or Resume.

export type JobKind = "full-audit" | "competitors" | "tab";
export type JobStatus = "queued" | "running" | "done" | "error" | "interrupted" | "canceled";

export interface JobProgress { total: number; completed: number; doneIds: string[]; message: string }
export interface CrawlJobSummary {
  id: string; kind: JobKind; tabId: string | null; status: JobStatus;
  progress: JobProgress; error: string | null; updatedAt: string;
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  const data = await res.json().catch(() => ({})) as Record<string, unknown>;
  if (!res.ok) throw new Error((data.error as string) || `Request failed (${res.status})`);
  return data as T;
}

// Start (or reattach to an already-running) job. Returns its id + status.
export async function startCrawlJob(input: {
  siteKey: string; kind: JobKind; tabId?: string | null; params: Record<string, unknown>;
}): Promise<{ jobId: string; status: JobStatus }> {
  return api<{ jobId: string; status: JobStatus }>("/api/crawl-jobs", {
    method: "POST",
    body: JSON.stringify({ siteKey: input.siteKey, kind: input.kind, tabId: input.tabId ?? null, params: input.params }),
  });
}

export async function listActiveJobs(siteKey: string): Promise<CrawlJobSummary[]> {
  const { jobs } = await api<{ jobs: CrawlJobSummary[] }>(`/api/crawl-jobs?siteKey=${encodeURIComponent(siteKey)}`);
  return jobs ?? [];
}

export async function resumeCrawlJob(jobId: string): Promise<void> {
  await api(`/api/crawl-jobs/${encodeURIComponent(jobId)}/resume`, { method: "POST" });
}

export async function cancelCrawlJob(jobId: string): Promise<void> {
  await api(`/api/crawl-jobs/${encodeURIComponent(jobId)}/cancel`, { method: "POST" });
}

// Pause a running job (keeps what's crawled; resumable). Use the fetch form for in-app
// actions (e.g. logout) where we can await it.
export async function pauseCrawlJob(jobId: string): Promise<void> {
  await api(`/api/crawl-jobs/${encodeURIComponent(jobId)}/pause`, { method: "POST" });
}

// Fire-and-forget pause for the page-unload path (tab close / refresh). sendBeacon
// survives the document being torn down, where a normal fetch would be aborted; it sends
// same-origin cookies so the auth session is intact. Falls back to a keepalive fetch.
export function pauseCrawlJobBeacon(jobId: string): void {
  const url = `/api/crawl-jobs/${encodeURIComponent(jobId)}/pause`;
  try {
    if (navigator.sendBeacon && navigator.sendBeacon(url, new Blob([], { type: "text/plain" }))) return;
  } catch { /* fall through */ }
  try { void fetch(url, { method: "POST", credentials: "include", keepalive: true }); } catch { /* ignore */ }
}

export interface JobStreamHandlers {
  onStatus?: (status: JobStatus, progress: JobProgress) => void;
  onProgress?: (message: string) => void;
  onSection?: (id: string, html: string) => void;
  onDone?: () => void;
  onError?: (message: string) => void;
  onCanceled?: () => void;
  onInterrupted?: () => void;
}

// Subscribe to a job's event stream. The server replays current status + every saved
// section on connect (so a reattach repaints), then streams live events. Returns a close
// fn that ONLY stops listening — it does not stop the server-side job (use cancelCrawlJob
// for that). EventSource auto-reconnects on transient drops; we close on terminal events.
export function subscribeCrawlJob(jobId: string, handlers: JobStreamHandlers): () => void {
  const es = new EventSource(`/api/crawl-jobs/${encodeURIComponent(jobId)}/stream`, { withCredentials: true });
  let closed = false;
  const close = () => { if (!closed) { closed = true; es.close(); } };

  es.onmessage = (e: MessageEvent<string>) => {
    // `progress` is overloaded by the server: a STRING for a progress message
    // (send({progress:msg})) or an OBJECT for a status snapshot (send({status,progress})).
    let d: {
      status?: JobStatus; progress?: string | JobProgress;
      id?: string; html?: string;
      done?: boolean; error?: string; canceled?: boolean; interrupted?: boolean;
    };
    try { d = JSON.parse(e.data); } catch { return; }
    if (typeof d.progress === "string") handlers.onProgress?.(d.progress);
    else if (d.status && d.progress) handlers.onStatus?.(d.status, d.progress);
    if (d.id && typeof d.html === "string") handlers.onSection?.(d.id, d.html);
    if (d.done)        { handlers.onDone?.();          close(); }
    if (d.error)       { handlers.onError?.(d.error);  close(); }
    if (d.canceled)    { handlers.onCanceled?.();      close(); }
    if (d.interrupted) { handlers.onInterrupted?.();   close(); }
  };
  es.onerror = () => { /* EventSource auto-retries; nothing to do unless we've closed */ };
  return close;
}
